import videojs from "./videojs-core.js";
import { ensureImaSdk } from "./assets-loader.js";
import { loadVidvertoFromSnippet } from "../ads/vidverto-loader.js";
import { createLogger } from "../helpers/logger.js";

import { registerSkipComponents } from "../ui/skip-components.js";

registerSkipComponents();

function runWhenIdle(callback, timeout = 2500) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, timeout);
  }
}

function getControlBarChildren(settings) {
  const children = ["playToggle"];

  if (settings?.skipButtonsBackward) {
    children.push("BackwardButton");
  }

  if (settings?.skipButtonsForward) {
    children.push("ForwardButton");
  }

  children.push("volumePanel", "progressControl", "fullscreenToggle");

  if (settings?.snapshot && videojs.getComponent("SnapshotButton")) {
    children.push("SnapshotButton");
  }

  return children;
}

function getControlBarOptions(settings) {
  return {
    children: getControlBarChildren(settings),
    volumePanel: {
      inline: false,
    },
    BackwardButton: {
      seconds: settings?.skipButtonsBackward,
    },
    ForwardButton: {
      seconds: settings?.skipButtonsForward,
    },
  };
}

export async function initPlayer(
  settings,
  {
    videoElementId = "videoPlayer",
    playerContainerSelector = ".player-container",
    wrapperSelector = ".video-wrapper",
    imaOptions = null,
    debug = false,
  } = {},
) {
  const log = createLogger("PlayerInit", debug);
  // burstIframes();

  const videoEl = document.getElementById(videoElementId);

  if (!videoEl) {
    return { player: null, dom: { playerContainer: null, wrapper: null } };
  }

  const playerContainer = videoEl.closest(playerContainerSelector) || null;
  const wrapper =
    videoEl.closest(wrapperSelector) ||
    playerContainer?.closest(wrapperSelector) ||
    null;

  if (imaOptions) {
    await ensureImaSdk();
  }

  let player = videojs.getPlayer(videoEl.id);
  const reused = !!(player && !player.isDisposed?.());

  if (reused) {
    log.debug("♻️ Reusing existing player:", videoEl.id);
  } else {
    if (settings?.snapshot) {
      await import("../ui/snapshotButton.js");
    }

    if (settings?.logoImageUrl) {
      await import("../ui/logo-overlay.js");
    }
    player = videojs(videoEl, {
      ...settings,
      fluid: settings?.fluid ?? true,
      aspectRatio: settings?.aspectRatio || "16:9",
      muted: true,
      controlBar: getControlBarOptions(settings),
      html5: {
        vhs: { overrideNative: true, enableLowInitialPlaylist: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    });
  }

  if (
    imaOptions &&
    typeof player.ima === "function" &&
    !player.__imaInitialized
  ) {
    const opts = {
      id: videoElementId,
      requestMode: imaOptions?.requestMode || "manual",
      vastLoadTimeout: imaOptions?.vastLoadTimeout ?? 8000,
      contribAdsSettings: {
        timeout: imaOptions?.contribAdsTimeout ?? 8000,
        prerollTimeout: imaOptions?.contribAdsPrerollTimeout ?? 8000,
        postrollTimeout: imaOptions?.contribAdsPostrollTimeout ?? 8000,
        ...(imaOptions?.contribAdsSettings || {}),
      },
      adsRenderingSettings: {
        loadVideoTimeout: imaOptions?.loadVideoTimeout ?? 8000,
        ...(imaOptions?.adsRenderingSettings || {}),
      },
    };

    if (imaOptions?.adTagUrl) {
      opts.adTagUrl = imaOptions.adTagUrl;
    }

    player.ima(opts);
    player.__imaInitialized = true;
  }

  const hasPreroll =
    Array.isArray(settings?.ads?.preroll) && settings.ads.preroll.length > 0;
  const hasFallback =
    Array.isArray(settings?.ads?.fallback) && settings.ads.fallback.length > 0;

  if (!hasPreroll && !hasFallback) {
    log.debug("No ads available");

    if (wrapper && settings?.ads?.passback) {
      loadVidvertoFromSnippet(wrapper, settings.ads.passback);
    }
  }

  if (settings.logoImageUrl && typeof player.logoOverlay === "function") {
    player.logoOverlay({
      imageUrl: settings.logoImageUrl,
      imageClickUrl: settings.logoImageClickUrl,
      imageTarget: settings.logoImageTarget,
      imagePosition: settings.logoImagePosition,
      imageOpacity: settings.logoImageOpacity,
      imageMargin: settings.logoImageMargin,
      width: settings.logoWidth || 80,
      zIndex: settings.logoImagezIndex ?? 2147483647,
    });
  }

  player.ready(() => {
    if (player.__destroying || player.isDisposed?.() || !player.el?.()) return;

    // const controlBar = player.getChild("controlBar");
    // if (!controlBar) {
    //   return;
    // }
    // const children = controlBar.children().slice();
    // children.forEach((child) => {
    // 	controlBar.removeChild(child);
    // });

    // controlBar.addChild("playToggle", {});
    // if (settings.skipButtonsBackward) {
    // 	controlBar.addChild("BackwardButton", {
    // 	  seconds: settings.skipButtonsBackward,
    // 	});
    // }
    // if (settings.skipButtonsForward) {
    // 	controlBar.addChild("ForwardButton", {
    // 	  seconds: settings.skipButtonsForward,
    // 	});
    // }
    // controlBar.addChild("volumePanel", { inline: false });
    // controlBar.addChild("progressControl", {});
    // controlBar.addChild("fullscreenToggle", {});
    // if (settings?.snapshot && videojs.getComponent("SnapshotButton")) {
    //   controlBar.addChild("SnapshotButton", {});
    // }

    if (settings?.muted) {
      player.muted(true);
      const volumePanel = player.getChild("controlBar").getChild("volumePanel");
      if (volumePanel) {
        const muteToggle = volumePanel.getChild("muteToggle");
        if (muteToggle) {
          muteToggle.update();
        }
      }
    }

    if (settings?.autoplay) {
      const v = player.el()?.querySelector("video");
      if (v) {
        v.muted = true;
        v.setAttribute("muted", "");
        v.playsInline = true;
        v.setAttribute("playsinline", "");
        try {
          v.volume = 0;
        } catch {}
      }
      player.muted(true);

      if (!player.__lockContent) {
        player.play().catch(() => false);
      }
    }

    let viewportWidth = window.innerWidth;

    try {
      if (window.self !== window.top) {
        viewportWidth = window.top.innerWidth || viewportWidth;
      }
    } catch (e) {}

    const isMobile = viewportWidth <= 768;

    if (!settings?.fluid) {
      player.fluid(false);
      if (isMobile) {
        player.width(settings?.mobileWidth);
        player.height(settings?.mobileHeight);
        if (wrapper) {
          wrapper.style.width = settings?.mobileWidth + "px";
          wrapper.style.height = settings?.mobileHeight + "px";
        }
      } else {
        player.width(settings?.desktopWidth);
        player.height(settings?.desktopHeight);
        if (wrapper) {
          wrapper.style.width = settings?.desktopWidth + "px";
          wrapper.style.height = settings?.desktopHeight + "px";
        }
      }
      if (wrapper) {
        wrapper.classList.add("video-wrapper-fixed");
        wrapper.classList.remove("video-wrapper-fluid");
      }
      if (playerContainer) {
        playerContainer.classList.add("player-fixed");
        playerContainer.classList.remove("player-fluid");
      }
    } else {
      player.fluid(true);
      if (settings?.aspectRatio) player.aspectRatio(settings.aspectRatio);
      if (wrapper) {
        wrapper.classList.remove("video-wrapper-fixed");
        wrapper.classList.add("video-wrapper-fluid");
      }
      if (playerContainer) {
        playerContainer.classList.remove("player-fixed");
        playerContainer.classList.add("player-fluid");
      }
    }

    let isSticky = false;
    let hasBeenVisible = false;
    let stickyDismissed = false;
    let hasStickyOnce = false;
    let stickyScrollHandler = null;

    if (
      (isMobile && settings?.stickyMobile) ||
      (!isMobile && settings?.stickyDesktop)
    ) {
      const inIframe = window.self !== window.top;
      let stickyTarget = null;

      try {
        stickyTarget = inIframe ? window.frameElement : playerContainer;
      } catch {
        stickyTarget = playerContainer;
      }

      let inlineAnchor =
        stickyTarget?.previousElementSibling?.classList?.contains(
          "cleon-inline-anchor",
        )
          ? stickyTarget.previousElementSibling
          : null;

      function getStickyObserveTarget() {
        if (inIframe) {
          return (
            stickyTarget?.__cleonVisualPlacement?.spacer || inlineAnchor || null
          );
        }

        return inlineAnchor || wrapper;
      }
      
      let iframeAncestorStyleRestore = [];

      function resetIframeStickyAncestors() {
        if (!inIframe || !stickyTarget || iframeAncestorStyleRestore.length)
          return;

        const parentDoc = stickyTarget.ownerDocument;
        if (!parentDoc?.body) return;

        let el = stickyTarget.parentElement;
        const patched = [];

        while (
          el &&
          el !== parentDoc.body &&
          el !== parentDoc.documentElement
        ) {
          const computed = parentDoc.defaultView?.getComputedStyle?.(el);
          const className = String(el.className || "");

          const canTrapFixed =
            className.includes("GoogleActiveViewElement") ||
            computed?.transform !== "none" ||
            computed?.filter !== "none" ||
            computed?.perspective !== "none" ||
            computed?.contain?.includes("paint") ||
            computed?.contain?.includes("layout") ||
            computed?.willChange?.includes("transform") ||
            computed?.overflow !== "visible" ||
            computed?.overflowX !== "visible" ||
            computed?.overflowY !== "visible";

          if (canTrapFixed) {
            patched.push({
              el,
              transform: el.style.transform || "",
              filter: el.style.filter || "",
              perspective: el.style.perspective || "",
              contain: el.style.contain || "",
              willChange: el.style.willChange || "",
              overflow: el.style.overflow || "",
              overflowX: el.style.overflowX || "",
              overflowY: el.style.overflowY || "",
            });

            el.style.setProperty("transform", "none", "important");
            el.style.setProperty("filter", "none", "important");
            el.style.setProperty("perspective", "none", "important");
            el.style.setProperty("contain", "none", "important");
            el.style.setProperty("will-change", "auto", "important");
            el.style.setProperty("overflow", "visible", "important");
            el.style.setProperty("overflow-x", "visible", "important");
            el.style.setProperty("overflow-y", "visible", "important");
          }

          el = el.parentElement;
        }

        iframeAncestorStyleRestore = patched;

        if (patched.length) {
          log.debug("📌 Iframe sticky ancestor styles relaxed", {
            patchedAncestors: patched.map(({ el }) => ({
              tag: el.tagName,
              id: el.id || "",
              className: String(el.className || ""),
            })),
          });
        }
      }

      function restoreIframeStickyAncestors() {
        if (!iframeAncestorStyleRestore.length) return;

        iframeAncestorStyleRestore.forEach(
          ({
            el,
            transform,
            filter,
            perspective,
            contain,
            willChange,
            overflow,
            overflowX,
            overflowY,
          }) => {
            if (!el?.style) return;
            el.style.transform = transform;
            el.style.filter = filter;
            el.style.perspective = perspective;
            el.style.contain = contain;
            el.style.willChange = willChange;
            el.style.overflow = overflow;
            el.style.overflowX = overflowX;
            el.style.overflowY = overflowY;
          },
        );

        iframeAncestorStyleRestore = [];
      }

      if (wrapper && playerContainer && stickyTarget) {
        let stickyObserver = null;
        let autoCloseTimer = null;
        let stickyReattachTimer = null;

        function cleanupSticky() {
          stickyDismissed = true;
          restoreIframeStickyAncestors();

          // Clean up scroll event listener
          if (stickyScrollHandler && stickyScrollWin) {
            try {
              stickyScrollWin.removeEventListener("scroll", stickyScrollHandler);
            } catch (e) {
              log.debug("Failed to remove scroll event listener:", e);
            }
            stickyScrollHandler = null;
          }

          if (stickyObserver) {
            stickyObserver.disconnect();
            stickyObserver = null;
          }

          if (autoCloseTimer) {
            clearTimeout(autoCloseTimer);
            autoCloseTimer = null;
          }

          if (stickyReattachTimer) {
            clearTimeout(stickyReattachTimer);
            stickyReattachTimer = null;
          }

          removeCloseButton();

          wrapper?.classList?.remove("is-sticky-active");

          stickyTarget?.classList?.remove(
            "sticky-top-left",
            "sticky-top-right",
            "sticky-bottom-left",
            "sticky-bottom-right",
          );

          isSticky = false;
        }

        const originalLayout = {
          isFluid: !!settings?.fluid,
          aspectRatio: settings?.aspectRatio || "16:9",

          wrapperClass: wrapper.classList.contains("video-wrapper-fluid")
            ? "video-wrapper-fluid"
            : "video-wrapper-fixed",

          containerClass: playerContainer.classList.contains("player-fluid")
            ? "player-fluid"
            : "player-fixed",

          wrapperStyle: {
            width: wrapper.style.width || "",
            height: wrapper.style.height || "",
          },

          containerStyle: {
            width: stickyTarget.style.width || "",
            height: stickyTarget.style.height || "",
            top: stickyTarget.style.top || "",
            right: stickyTarget.style.right || "",
            bottom: stickyTarget.style.bottom || "",
            left: stickyTarget.style.left || "",
            position: stickyTarget.style.position || "",
            zIndex: stickyTarget.style.zIndex || "",
            transform: stickyTarget.style.transform || "",
            maxWidth: stickyTarget.style.maxWidth || "",
            maxHeight: stickyTarget.style.maxHeight || "",
          },

          playerSize: isMobile
            ? {
                width: settings?.mobileWidth,
                height: settings?.mobileHeight,
              }
            : {
                width: settings?.desktopWidth,
                height: settings?.desktopHeight,
              },
          stickySize: isMobile
            ? {
                width: settings?.stickyMobileWidth || 320,
                height: settings?.stickyMobileHeight || 180,
              }
            : {
                width: settings?.stickyDesktopWidth || 320,
                height: settings?.stickyDesktopHeight || 180,
              },
        };

        stickyObserver = new IntersectionObserver(handleStickyObserver, {
          threshold: [0, 0.05, 0.7],
          rootMargin: "0px 0px -15% 0px",
        });

        const observeTarget = getStickyObserveTarget();

        if (!observeTarget) {
          log.debug(
            "⏭ No safe sticky observe target; sticky disabled for iframe",
          );
          return;
        }

        stickyObserver.observe(observeTarget);

        function makeSticky() {
          resetIframeStickyAncestors();
          stickyTarget?.__cleonVisualPlacement?.setSticky?.(true);

          const stickyPos = isMobile
            ? settings?.stickyPositionMobile
            : settings?.stickyPositionDesktop;

          const vOffset =
            Number(
              isMobile
                ? settings?.verticalOffset_mobile
                : settings?.verticalOffset_desktop,
            ) || 0;

          const hOffset =
            Number(
              isMobile
                ? settings?.horizontalOffset_mobile
                : settings?.horizontalOffset_desktop,
            ) || 0;

          log.debug("📦 Making sticky →", stickyPos, { vOffset, hOffset });
          stickyTarget.classList.add(`sticky-${stickyPos}`);

          stickyTarget.style.setProperty("position", "fixed", "important");
          stickyTarget.style.setProperty("z-index", "999999", "important");
          stickyTarget.style.top = "";
          stickyTarget.style.right = "";
          stickyTarget.style.bottom = "";
          stickyTarget.style.left = "";
          stickyTarget.style.transform = "";

          const stickyWidth = originalLayout.stickySize?.width;
          const stickyHeight = originalLayout.stickySize?.height;

          if (stickyWidth) {
            stickyTarget.style.width = /^\d+$/.test(String(stickyWidth))
              ? `${stickyWidth}px`
              : String(stickyWidth);
          }

          if (stickyHeight) {
            stickyTarget.style.height = /^\d+$/.test(String(stickyHeight))
              ? `${stickyHeight}px`
              : String(stickyHeight);
          }

          stickyTarget.style.maxWidth = "none";
          stickyTarget.style.maxHeight = "none";

          switch (stickyPos) {
            case "top-left":
              stickyTarget.style.top = `${vOffset}px`;
              stickyTarget.style.left = `${hOffset}px`;
              break;

            case "top-right":
              stickyTarget.style.top = `${vOffset}px`;
              stickyTarget.style.right = `${hOffset}px`;
              break;

            case "bottom-left":
              stickyTarget.style.bottom = `${vOffset}px`;
              stickyTarget.style.left = `${hOffset}px`;
              break;

            case "bottom-right":
            default:
              stickyTarget.style.bottom = `${vOffset}px`;
              stickyTarget.style.right = `${hOffset}px`;
              break;
          }

          addCloseButton();

          if (settings?.outstreamAutoClose) {
            if (autoCloseTimer) clearTimeout(autoCloseTimer);
            autoCloseTimer = setTimeout(
              () => {
                log.debug("⏱ Auto closing sticky player");
                closePlayer();
              },
              Number(settings?.outstreamAutoCloseDelay) || 10000,
            );
          }

          wrapper.classList.add("is-sticky-active");
        }

        function closePlayer(manual = false) {
          log.debug(
            "⏹ Closing sticky player...",
            manual ? "(manual)" : "(auto)",
          );

          const stickyPos = isMobile
            ? settings?.stickyPositionMobile
            : settings?.stickyPositionDesktop;

          if (manual) {
            stickyDismissed = true;
          }
          // Stop ad if running via IMA SDK
          // if (
          //   manual &&
          //   player.ima &&
          //   typeof player.ima.pauseAd === "function"
          // ) {
          //   try {
          //     player.ima.pauseAd();
          //   } catch (e) {
          //     log.debug("IMA pauseAd failed:", e);
          //   }
          // }

          // if (manual) {
          //   player.pause();
          // }

          // Remove sticky styling
          stickyTarget.classList.remove(
            "sticky-top-left",
            "sticky-top-right",
            "sticky-bottom-left",
            "sticky-bottom-right",
          );

          removeCloseButton();

          // Disconnect observer for now
          if (stickyObserver) {
            stickyObserver.disconnect();
            stickyObserver = null;
          }

          // Fully restore layout
          restorePlayerLayout();

          if (!manual) {
            hasBeenVisible = false;
          

            stickyReattachTimer = setTimeout(() => {
              if (
                player.__destroying ||
                player.isDisposed?.() ||
                !player.el?.() ||
                !stickyTarget?.isConnected
              )
                return;
              if (!stickyObserver) {
                log.debug("♻ Reattaching sticky observer after auto-close...");
                stickyObserver = new IntersectionObserver(
                  handleStickyObserver,
                  {
                    threshold: [0, 0.05, 0.7],
                    rootMargin: "0px 0px -15% 0px",
                  },
                );
                const observeTarget = getStickyObserveTarget();

                if (!observeTarget) {
                  log.debug(
                    "⏭ No safe sticky observe target; sticky disabled for iframe",
                  );
                  return;
                }

                stickyObserver.observe(observeTarget);
              }
            }, 800);
          }

          if (autoCloseTimer) {
            clearTimeout(autoCloseTimer);
            autoCloseTimer = null;
          }

          isSticky = false;
          setLogoOverlayVisible(true);
          log.debug(
            manual
              ? "❌ Sticky closed manually, won't reappear."
              : "✅ Sticky auto-closed, will reappear when scrolled back.",
          );
        }

        function forceImaResize() {
          try {
            if (player?.ima && typeof player.ima.resize === "function") {
              const w = stickyTarget?.clientWidth || 0;
              const h = stickyTarget?.clientHeight || 0;
              player.ima.resize(w, h);
            }
          } catch {}
        }

        function restorePlayerLayout() {
          wrapper.classList.remove("is-sticky-active");

          const stickyPos =
            (isMobile
              ? settings?.stickyPositionMobile
              : settings?.stickyPositionDesktop) || "bottom-right";

          stickyTarget.classList.remove(
            "sticky-top-left",
            "sticky-top-right",
            "sticky-bottom-left",
            "sticky-bottom-right",
          );

          restoreIframeStickyAncestors();

          if (
            !inIframe &&
            inlineAnchor?.isConnected &&
            stickyTarget &&
            inlineAnchor.nextSibling !== stickyTarget
          ) {
            inlineAnchor.after(stickyTarget);
          }

          const restoredWidth = isMobile
            ? settings?.mobileWidth
            : settings?.desktopWidth;
          const restoredHeight = isMobile
            ? settings?.mobileHeight
            : settings?.desktopHeight;

          const toCssSize = (value) =>
            /^\d+$/.test(String(value)) ? `${value}px` : String(value);

          if (!inIframe) {
            stickyTarget.style.position =
              originalLayout.containerStyle.position;
            stickyTarget.style.zIndex = originalLayout.containerStyle.zIndex;
            stickyTarget.style.width = restoredWidth
              ? toCssSize(restoredWidth)
              : originalLayout.containerStyle.width;
            stickyTarget.style.height = restoredHeight
              ? toCssSize(restoredHeight)
              : originalLayout.containerStyle.height;
            stickyTarget.style.top = originalLayout.containerStyle.top;
            stickyTarget.style.right = originalLayout.containerStyle.right;
            stickyTarget.style.bottom = originalLayout.containerStyle.bottom;
            stickyTarget.style.left = originalLayout.containerStyle.left;
            stickyTarget.style.transform =
              originalLayout.containerStyle.transform || "";
            stickyTarget.style.maxWidth =
              originalLayout.containerStyle.maxWidth;
            stickyTarget.style.maxHeight =
              originalLayout.containerStyle.maxHeight;

            wrapper.classList.remove(
              "video-wrapper-fluid",
              "video-wrapper-fixed",
            );
            playerContainer.classList.remove("player-fluid", "player-fixed");

            wrapper.classList.add(originalLayout.wrapperClass);
            playerContainer.classList.add(originalLayout.containerClass);

            wrapper.style.width = originalLayout.wrapperStyle.width;
            wrapper.style.height = originalLayout.wrapperStyle.height;

            playerContainer.style.width = restoredWidth
              ? toCssSize(restoredWidth)
              : originalLayout.containerStyle.width;
            playerContainer.style.height = restoredHeight
              ? toCssSize(restoredHeight)
              : originalLayout.containerStyle.height;
            playerContainer.style.top = originalLayout.containerStyle.top;
            playerContainer.style.right = originalLayout.containerStyle.right;
            playerContainer.style.bottom = originalLayout.containerStyle.bottom;
            playerContainer.style.left = originalLayout.containerStyle.left;

            if (originalLayout.isFluid) {
              player.fluid(true);
              if (originalLayout.aspectRatio) {
                player.aspectRatio(originalLayout.aspectRatio);
              }
            } else {
              player.fluid(false);
              player.width(originalLayout.playerSize.width);
              player.height(originalLayout.playerSize.height);
            }

            requestAnimationFrame(() => {
              forceImaResize();
              log.debug("🎯 Layout fully restored (no sticky).");
            });
            } else {
              requestAnimationFrame(() => {
                stickyTarget?.__cleonVisualPlacement?.setSticky?.(false);
                stickyTarget?.__cleonVisualPlacement?.restoreArticlePosition?.();

                stickyTarget.style.position = originalLayout.containerStyle.position;
                stickyTarget.style.zIndex = originalLayout.containerStyle.zIndex;
                stickyTarget.style.width = originalLayout.containerStyle.width;
                stickyTarget.style.height = originalLayout.containerStyle.height;
                stickyTarget.style.top = originalLayout.containerStyle.top;
                stickyTarget.style.right = originalLayout.containerStyle.right;
                stickyTarget.style.bottom = originalLayout.containerStyle.bottom;
                stickyTarget.style.left = originalLayout.containerStyle.left;
                stickyTarget.style.transform = originalLayout.containerStyle.transform || "";
                stickyTarget.style.maxWidth = originalLayout.containerStyle.maxWidth;
                stickyTarget.style.maxHeight = originalLayout.containerStyle.maxHeight;

                forceImaResize();
                log.debug("🎯 Iframe layout restored (no sticky).");
              });
            }
        }

        function addCloseButton() {
          if (playerContainer.querySelector(".outstream-close")) return;

          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "outstream-close";
          btn.setAttribute("aria-label", "Close");
          btn.innerHTML = `
						<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
							<path d="M6 6L18 18M18 6L6 18"
								  stroke="currentColor"
								  stroke-width="2"
								  stroke-linecap="round"/>
						</svg>
					`;

          // Store event handlers for cleanup
          btn.__cleonEventHandlers = [];

          function stopCloseEvent(e) {
            e.preventDefault();
            e.stopPropagation();

            if (typeof e.stopImmediatePropagation === "function") {
              e.stopImmediatePropagation();
            }
          }

          const eventHandler = (e) => {
            stopCloseEvent(e);

            if (e.type === "click") {
              log.debug("✖ Manual close clicked");
              closePlayer(true);
            }
          };

          [
            "pointerdown",
            "mousedown",
            "mouseup",
            "touchstart",
            "touchend",
            "click",
            "dblclick",
          ].forEach((eventName) => {
            btn.addEventListener(eventName, eventHandler, true);
            btn.__cleonEventHandlers.push({ eventName, handler: eventHandler });
          });

          playerContainer.appendChild(btn);
        }

        function removeCloseButton() {
          const btn = playerContainer.querySelector(".outstream-close");
          if (btn && btn.__cleonEventHandlers) {
            // Remove all event listeners
            btn.__cleonEventHandlers.forEach(({ eventName, handler }) => {
              btn.removeEventListener(eventName, handler, true);
            });
            btn.__cleonEventHandlers = null;
          }
          if (btn) btn.remove();
        }

        if (typeof player.__cleanupSticky === "function") {
          try {
            player.__cleanupSticky();
          } catch {}
        }

        player.__cleanupSticky = cleanupSticky;
        player.one("dispose", cleanupSticky);

        let stickyFlipLock = false;
        let ignoreStickyObserverUntil = 0;

		let lastStickyScrollY = 0;
		let stickyScrollDirection = "down";
		let stickyScrollWin = window;

		try {
			stickyScrollWin = inIframe
				? stickyTarget?.ownerDocument?.defaultView || window
				: window;

			lastStickyScrollY = stickyScrollWin.scrollY || 0;

			stickyScrollHandler = () => {
				const currentY = stickyScrollWin.scrollY || 0;

				if (currentY < lastStickyScrollY) {
					stickyScrollDirection = "up";
				} else if (currentY > lastStickyScrollY) {
					stickyScrollDirection = "down";
				}

				lastStickyScrollY = currentY;
			};

			stickyScrollWin.addEventListener("scroll", stickyScrollHandler, { passive: true });
		} catch {}

        function beginIgnoreObserver(ms = 300) {
          ignoreStickyObserverUntil = Date.now() + ms;
        }
        function lockFlip(ms = 250) {
          stickyFlipLock = true;
          setTimeout(() => (stickyFlipLock = false), ms);
        }

        function handleStickyObserver(entries) {
			if (Date.now() < ignoreStickyObserverUntil) return;
			if (player.__destroying || player.isDisposed?.() || !player.el?.()) return;
			if (stickyDismissed) return;

			const entry = entries[0];
			if (!entry) return;

			const ratio = entry.intersectionRatio;

			if (inIframe && entry.target === stickyTarget) {
				log.debug("⏭ Ignoring sticky observer event from stickyTarget iframe");
				return;
			}

			const isIframeVisualPlacement =
				inIframe && !!stickyTarget?.__cleonVisualPlacement?.spacer;

			if (isIframeVisualPlacement) {
				const rect = entry.boundingClientRect;
				const rootHeight =
				entry.rootBounds?.height ||
				stickyScrollWin?.innerHeight ||
				window.innerHeight ||
				document.documentElement.clientHeight ||
				0;

				const stickyTriggerLine = 0;
				const restoreLine = Math.round(rootHeight * 0.35);

				const isSpacerVisible = rect.bottom > 0 && rect.top < rootHeight;

				log.debug("🧪 iframe visual sticky check", {
				ratio,
				top: Math.round(rect.top),
				bottom: Math.round(rect.bottom),
				rootHeight,
				restoreLine,
				hasBeenVisible,
				isSticky,
				scrollDirection: stickyScrollDirection,
				isSpacerVisible,
				});

				if (isSpacerVisible) {
				hasBeenVisible = true;
				}

				if (
				hasBeenVisible &&
				!isSticky &&
				!stickyFlipLock &&
				rect.bottom <= stickyTriggerLine
				) {
				if (settings?.stickyOnce && hasStickyOnce) return;

				lockFlip(300);
				beginIgnoreObserver(350);

				hasStickyOnce = true;
				isSticky = true;
				setLogoOverlayVisible(false);
				makeSticky();
				return;
				}

				if (
				isSticky &&
				!stickyFlipLock &&
				rect.bottom > 0 &&
				rect.top < restoreLine
				) {
				lockFlip(300);
				beginIgnoreObserver(350);

				const stickyPos = isMobile
					? settings?.stickyPositionMobile
					: settings?.stickyPositionDesktop;

				if (autoCloseTimer) {
					clearTimeout(autoCloseTimer);
					autoCloseTimer = null;
				}

				stickyTarget.classList.remove(
          "sticky-top-left",
          "sticky-top-right",
          "sticky-bottom-left",
          "sticky-bottom-right",
        );
				removeCloseButton();
				restorePlayerLayout();
				isSticky = false;
				setLogoOverlayVisible(true);
				return;
				}

				return;
			}

			if (ratio >= 0.7) {
				hasBeenVisible = true;

				if (isSticky && !stickyFlipLock) {
				lockFlip(300);
				beginIgnoreObserver(350);

				const stickyPos = isMobile
					? settings?.stickyPositionMobile
					: settings?.stickyPositionDesktop;

				if (autoCloseTimer) {
					clearTimeout(autoCloseTimer);
					autoCloseTimer = null;
				}

				stickyTarget.classList.remove(
          "sticky-top-left",
          "sticky-top-right",
          "sticky-bottom-left",
          "sticky-bottom-right",
        );
				removeCloseButton();
				restorePlayerLayout();
				isSticky = false;
				setLogoOverlayVisible(true);
				}

				return;
			}

			if (hasBeenVisible && !isSticky && ratio <= 0.05) {
				if (settings?.stickyOnce && hasStickyOnce) return;
				if (stickyFlipLock) return;

				lockFlip(300);
				beginIgnoreObserver(350);

				hasStickyOnce = true;
				isSticky = true;
				setLogoOverlayVisible(false);
				makeSticky();
			}
			}

        function setLogoOverlayVisible(visible) {
          const logoEl = player.el()?.querySelector(".vjs-logo-overlay");
          if (!logoEl) return;
          logoEl.style.display = visible ? "block" : "none";
        }
      }
    }
  });
  return { player, dom: { playerContainer, wrapper } };
}

